import UntrackedModel from "../../../common/models/untracked/untrackedModel";
import Membership from "./membership";
import { RoleType } from "~/enums/roles/roleType";
import {IGroupRole, IRoleGeneral} from "~/interfaces/roleInterfaces"
import Capable from "../../../common/mixins/capable";
import { RelationType } from "~/models/relationType";
import { GroupRoleCapability } from "~/enums/roles/capabilities/groupRoleCapability";
import Selected from "../store/modules/selected";
import {IMemberModel, IMemberInvitation, IMembershipModel, IMembershipContainer, IOrganization} from '~/interfaces/organizationInterfaces'
import Application from '../../document_management/models/application';
import { GroupType } from "@/common/enums/groupType";

export default class Group extends Capable(UntrackedModel) implements IMembershipContainer {
  name: string;
  id: string;
  memberships: IMembershipModel[] = [];
  organization_id: string;
  applications: Application[] = [];
  group_type: GroupType

  static get displayName(): string {
    return 'Group';
  }

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    return {
      'memberships': { type: Membership, relationType: RelationType.array },
      'applications': { type: Application, relationType: RelationType.array },
    };
  }

  createEmbeddedObject = {
    applications: (parent, json) => new Application().loadFromJson(json),
  };

  get organization(): IOrganization {
    return this.parent;
  }

  get hasMemberships(): boolean {
    return this.memberships.length > 0;
  }

  findMembershipByEmail(email: string): IMembershipModel {
    return this.memberships.find(m => {
      const emailSource = m.member || m.invitation;
      return emailSource.email === email;
    });
  }

  members(): (IMemberModel | IMemberInvitation)[] {
    return this.memberships.map(membership => membership.member || membership.invitation);
  }

  memberEmails(): string[] {
    return this.getEmails(this.members());
  }

  pendingMemberships(): IMembershipModel[] {
    return this.memberships.filter(membership => membership.active === false);
  }

  pendingMembers(): (IMemberModel | IMemberInvitation)[] {
    return this.pendingMemberships().map(membership => membership.member || membership.invitation);
  }

  pendingMemberEmails(): string[] {
    return this.getEmails(this.pendingMembers());
  }

  fullMemberships(): IMembershipModel[] {
    return this.memberships.filter(membership => membership.active === true);
  }

  fullMembers(): IMemberModel[] {
    return this.fullMemberships().map(membership => membership.member);
  }

  fullMemberEmails(): string[] {
    return this.getEmails(this.fullMembers());
  }

  findMemberBy(key, value) {
    const filtered = this.members().filter(m => m !== undefined && m[key] === value);
    let member = null;
    if (filtered.length > 0) {
      member = filtered[0];
    }
    return member;
  }

  findFullMemberBy(key, value): IMemberModel {
    return this.fullMembers().find(member => member[key] === value);
  }

  getEmails(members: IMemberModel[]): string[] {
    return members.map(member => member.email);
  }

  findPendingMemberBy(key, value): IMemberModel {
    return this.pendingMembers().find(member => member[key] === value);
  }

  removeMembership({ email, id }: { email?: string, id?: string }): void {
    if (email) {
      const membership = this.findMembershipByEmail(email);
      if (membership) {
        id = membership.id;
      }
    }
    this.memberships = this.memberships.filter(m => m.id !== id);
  }

  get rolesForMembers(): IGroupRole[] {
    return this.organization.roles.filter(role => role.role_type === RoleType.group && role.id !== this.organization.group_owner_role_id) as IGroupRole[];
  }

  get ownerRoleId(): string {
    return this.organization.group_owner_role_id;
  }

  get ownerRole(): IRoleGeneral {
    return this.organization.roles.find(r => r.id === this.organization.group_owner_role_id);
  }

  get membershipOwner() {
    return this.memberships.find(m => m.role_ids.includes(this.ownerRoleId));
  }

  get roleManagementRoleId(): number {
    return GroupRoleCapability.manageRoles;
  }

  get organizationId() {
    return this.organization.id;
  }

  get capabilities() {
    const currentMembership = this.currentMembership
    if (currentMembership) {
      return currentMembership.capabilities
    } else {
      return []
    }
  }

  get currentMembership() {
    return this.findMembershipByEmail(Selected.currentUser.get().email)
  }

  get isGroup(): true {
    return true
  }

  get isOrganization(): false {
    return false
  }
}

